/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { useEffect, useRef } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { OrbitPoint } from '../../../shared/entities/haloNet';
import { selectSelectedSpaceVehicleSvName, selectVisibleSpaceVehicleOrbitsBySvName } from '../../../store';

export interface OrbitPathsContainerProps {
  map: google.maps.Map;
  isOnFullVehicleView?: boolean;
}

const polyLineBaseStyle = {
  geodesic: true,
  strokeColor: '#9FAFBC',
  strokeOpacity: 1.0,
  strokeWeight: 2,
  zIndex: 1,
};

const toPolylinePath = (orbitPoints: OrbitPoint[]) =>
  orbitPoints.map((orbitPoint: OrbitPoint) => ({
    lat: orbitPoint.position.latitude,
    lng: orbitPoint.position.longitude,
  }));

const OrbitPathsContainer = ({ map, isOnFullVehicleView = false }: OrbitPathsContainerProps) => {
  // Ref to store polylines keyed by svName. One space vehicle can have more than one datapath,
  // so a key of datapathUuid would stack two identical polylines on top of each other.
  const polylinesRef = useRef<Map<string, google.maps.Polyline>>(new Map());

  // The points each polyline was drawn from. The orbits poll replaces the six hour window
  // periodically, and without this a polyline would keep the old window while the marker
  // moves onto the new one.
  const drawnOrbitsRef = useRef<Map<string, OrbitPoint[]>>(new Map());

  // Selectors
  const orbits = useSelector(selectVisibleSpaceVehicleOrbitsBySvName, shallowEqual);
  const selectedSvName = useSelector(selectSelectedSpaceVehicleSvName);

  // Effect 1: Create/destroy polylines when orbit data or visibility changes
  useEffect(() => {
    if (!orbits) return;

    const currentPolylines = polylinesRef.current;
    const drawnOrbits = drawnOrbitsRef.current;

    const removePolyline = (polyline: google.maps.Polyline, svName: string) => {
      polyline.setMap(null);
      currentPolylines.delete(svName);
      drawnOrbits.delete(svName);
    };

    // Draws the polyline, or moves an existing one onto the newly fetched window.
    const drawPolyline = (svName: string, strokeOpacity: number) => {
      const orbitPoints = orbits[svName];
      const polyline = currentPolylines.get(svName);

      if (!polyline) {
        currentPolylines.set(
          svName,
          new google.maps.Polyline({ path: toPolylinePath(orbitPoints), map, ...polyLineBaseStyle, strokeOpacity }),
        );
      } else if (drawnOrbits.get(svName) !== orbitPoints) {
        polyline.setPath(toPolylinePath(orbitPoints));
      }

      drawnOrbits.set(svName, orbitPoints);
    };

    if (isOnFullVehicleView) {
      // In full vehicle view, only show the selected vehicle's orbit
      const shouldShow = !!selectedSvName && !!orbits[selectedSvName];

      // Remove all polylines that aren't the target
      currentPolylines.forEach((polyline, svName) => {
        if (svName !== selectedSvName || !shouldShow) {
          removePolyline(polyline, svName);
        }
      });

      // Draw the target polyline, or move it onto the newly fetched window
      if (shouldShow) {
        drawPolyline(selectedSvName, 1.0);
      }
    } else {
      // In normal view, show all non-hidden orbits
      const visibleSvNames = new Set(Object.keys(orbits));

      // Remove polylines that should no longer exist
      currentPolylines.forEach((polyline, svName) => {
        if (!visibleSvNames.has(svName)) {
          removePolyline(polyline, svName);
        }
      });

      // Draw the ones that are missing, and move the rest onto the newly fetched window
      visibleSvNames.forEach(svName => {
        drawPolyline(svName, !selectedSvName || selectedSvName === svName ? 1.0 : 0.2);
      });
    }
  }, [orbits, map, isOnFullVehicleView, selectedSvName]);

  // Effect 2: Update opacity when selection changes (no destroy/recreate)
  useEffect(() => {
    if (isOnFullVehicleView) return; // Full vehicle view always shows at 1.0

    polylinesRef.current.forEach((polyline, svName) => {
      const opacity = !selectedSvName || selectedSvName === svName ? 1.0 : 0.2;
      polyline.setOptions({ strokeOpacity: opacity });
    });
  }, [selectedSvName, isOnFullVehicleView]);

  // Cleanup on unmount
  useEffect(() => {
    const currentPolylines = polylinesRef.current;
    return () => {
      currentPolylines.forEach(polyline => {
        polyline.setMap(null);
      });
      currentPolylines.clear();
    };
  }, []);

  return <></>;
};

export default OrbitPathsContainer;
